import React, { useRef, useState } from "react";
import styled from "styled-components";
import {
    Button,
    StandardModalDescription,
    StandardModalSectionHeader,
    StandardModalSectionTitle,
    Toast,
    portalColors
} from "src/components";

import RemoveIcon from "src/img/notice/remove-icon.svg";
import FileIcon from "src/img/notice/document.svg";

const Wrapper = styled.div`
    position: relative;
    display: grid;
    gap: 0.9rem;
`;

const HiddenInput = styled.input`
    display: none;
`;

const BrowseButton = styled(Button)`
    width: 140px;
    height: 38px;
    background-color: ${portalColors.text};
    color: var(--calendar-bg, #FFFFFF);
    border-radius: 8px;
    border: 1px solid ${portalColors.text};
    cursor: pointer;
`;

const FileList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const AttachItemContainer = styled.div`
    width: min(100%, 292px);
    min-height: 46px;
    background: ${portalColors.surface};
    box-shadow: none;
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    display: flex;
    align-items: center;
    padding: 0 10px;
`;

const FileIconImg = styled.img`
    max-width: 25px;
    margin-right: 15px;
`;

const FileName = styled.span`
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RemoveImg = styled.img`
    margin-left: auto;
    height: 40px;
    cursor: pointer;
`;

const ATTACHMENT_LIMIT = 5;

const Attachment = ({
    uploadedAttachmentFiles,
    setUploadedAttachmentFiles,
    setDeleteAttachmentFiles,
    existingAttachments = [],
    setExistingAttachments = null
}) => {

    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleFiles = (files) => {
        const fileArray = Array.from(files);

        if (uploadedAttachmentFiles.length + fileArray.length > ATTACHMENT_LIMIT) {
            return Toast.info("You cannot add more attachment files.");
        }

        fileArray.forEach(file => {
            const exists = uploadedAttachmentFiles.find(f => f.name === file.name);

            if (exists) {
                Toast.error("Similar filename found");
                return;
            }

            setUploadedAttachmentFiles(prev => [
                ...prev,
                {
                    name: file.name,
                    type: file.type,
                    file: file
                }
            ]);
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
    };

    const handleRemoveAttachment = (file, isExisting) => {
        if (isExisting) {
            setExistingAttachments(prev =>
                prev.filter(f => f.uuid !== file.uuid)
            );
            setDeleteAttachmentFiles(prev => [...prev, file.uuid]);
        } else {
            setUploadedAttachmentFiles(prev =>
                prev.filter(f => f.name !== file.name)
            );
        }
    };

    return (
        <Wrapper>
            <StandardModalSectionHeader>
                <div>
                    <StandardModalSectionTitle>Attachments (Optional)</StandardModalSectionTitle>
                    <StandardModalDescription>
                        Select files to attach to this content for users to view and download.
                    </StandardModalDescription>
                </div>
            </StandardModalSectionHeader>
            <HiddenInput
                type="file"
                multiple
                ref={fileInputRef}
                onChange={(e) => handleFiles(e.target.files)}
            />

            <BrowseButton
                type="button"
                onClick={handleBrowseClick}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
            >
                {isDragging ? "Drop files here" : "Browse File"}
            </BrowseButton>

            <FileList>
                {uploadedAttachmentFiles.map(file => (
                    <AttachItemContainer key={file.name}>
                        <FileIconImg src={FileIcon} alt="File Icon" />
                        <FileName>{file.name}</FileName>
                        <RemoveImg
                            src={RemoveIcon}
                            alt="Remove"
                            onClick={() => handleRemoveAttachment(file, false)}
                        />
                    </AttachItemContainer>
                ))}

                {existingAttachments.map(file => (
                    <AttachItemContainer key={file.uuid}>
                        <FileName>{file.fileName}</FileName>
                        <RemoveImg
                            src={RemoveIcon}
                            alt="Remove"
                            onClick={() => handleRemoveAttachment(file, true)}
                        />
                    </AttachItemContainer>
                ))}
            </FileList>
        </Wrapper>
    );
};

export default Attachment;
